import React, { memo } from 'react'
import { Handle, Position, NodeProps } from '@xyflow/react'
import { AlertTriangle, User, HelpCircle, FileText, GitBranch, Link as LinkIcon, Shield } from 'lucide-react'
import { MapNode, Uncertainty } from '../lib/types'

const kindIcons: Record<string, React.ReactNode> = {
  delegation: <AlertTriangle size={13} />,
  human_responsibility: <User size={13} />,
  assumption: <HelpCircle size={13} />,
  evidence: <FileText size={13} />,
  decision: <GitBranch size={13} />,
  open_question: <HelpCircle size={13} />,
  system_constraint: <Shield size={13} />,
}

const uncertaintyColors: Record<Exclude<Uncertainty, null>, string> = {
  negligible: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  manageable: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  critical: 'bg-red-100 text-red-800 border-red-300',
}

interface CustomNodeData extends MapNode {
  selected?: boolean
}

export const WorkshopNode = memo(({ data, selected }: NodeProps<CustomNodeData>) => {
  const n = data as CustomNodeData
  const hasFour = !!(n.known && n.unknown && n.evidence_need && n.next_question)
  const u = n.uncertainty

  return (
    <div
      className={`node-card px-3 py-2.5 rounded-lg w-[238px] text-[13px] leading-snug select-none
        ${selected ? 'selected' : ''}
        ${u === 'critical' ? 'knot-critical' : ''}
        ${u === 'high' ? 'knot-high' : ''}`}
    >
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2 !h-2" />
      <Handle type="source" position={Position.Right} className="!bg-slate-400 !w-2 !h-2" />

      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-bench-accent shrink-0">
          {kindIcons[n.kind] || <LinkIcon size={13} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-bench-ink pr-1 line-clamp-2">{n.label || 'Untitled knot'}</div>

          <div className="mt-1.5 flex flex-wrap gap-1 items-center">
            {u && (
              <span className={`uncertainty-pill px-1.5 py-px rounded border ${uncertaintyColors[u]}`}>
                {u}
              </span>
            )}
            {hasFour && (
              <span className="uncertainty-pill px-1.5 py-px rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                REPAIRED
              </span>
            )}
            {!u && !hasFour && (
              <span className="text-[10px] text-slate-400">unlabeled</span>
            )}
          </div>

          {/* Mini status row for the four signals */}
          <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] text-slate-500">
            <div className={n.known ? 'text-emerald-700' : ''}>K {n.known ? '●' : '○'}</div>
            <div className={n.unknown ? 'text-amber-700' : ''}>U {n.unknown ? '●' : '○'}</div>
            <div className={n.evidence_need ? 'text-sky-700' : ''}>E {n.evidence_need ? '●' : '○'}</div>
            <div className={n.next_question ? 'text-violet-700' : ''}>? {n.next_question ? '●' : '○'}</div>
          </div>
        </div>
      </div>
    </div>
  )
})

WorkshopNode.displayName = 'WorkshopNode'
